import { getAIMessageRenderState } from "./messages/AIMessage";
import { TOOL_CONFIGS } from "./messages/utils/tool-calls";
import type { ChatMessage, ChatToolCall } from "./types";
import { getChatMessageType } from "./types";

export type GroupedToolCall = {
  id: string;
  toolCall: ChatToolCall;
  messageId?: string;
  messageIndex: number;
};

export type TranscriptEntry =
  | { type: "message"; key: string; message: ChatMessage; messageIndex: number }
  | {
      type: "group";
      key: string;
      calls: GroupedToolCall[];
      firstMessageIndex: number;
      lastMessageIndex: number;
    };

export const groupToolMessages = (
  messages: ChatMessage[]
): TranscriptEntry[] => {
  const entries: TranscriptEntry[] = [];
  let group: Extract<TranscriptEntry, { type: "group" }> | undefined;
  const finishGroup = () => {
    if (group) entries.push(group);
    group = undefined;
  };

  messages.forEach((message, messageIndex) => {
    const type = getChatMessageType(message);
    if (type === "tool") return;

    if (type !== "ai") {
      finishGroup();
      entries.push({
        type: "message",
        key: message.id ?? `${type}-${messageIndex}`,
        message,
        messageIndex,
      });
      return;
    }

    const state = getAIMessageRenderState(message);
    if (state.textContent.trim()) finishGroup();
    if (state.textContent.trim() || state.hasThinkingMessages) {
      entries.push({
        type: "message",
        key: `text-${message.id ?? messageIndex}`,
        message,
        messageIndex,
      });
    }

    const calls = state.toolCallBlocks
      .filter(
        (toolCall) => !toolCall.name || !TOOL_CONFIGS[toolCall.name]?.hidden
      )
      .map((toolCall, index) => ({
        id: toolCall.id ?? `${message.id ?? messageIndex}-${index}`,
        toolCall,
        messageId: message.id,
        messageIndex,
      }));
    if (calls.length > 0) {
      if (!group) {
        group = {
          type: "group",
          key: `group-${message.id ?? messageIndex}`,
          calls: [],
          firstMessageIndex: messageIndex,
          lastMessageIndex: messageIndex,
        };
      }
      group.calls.push(...calls);
      group.lastMessageIndex = messageIndex;
    }
  });
  finishGroup();
  return entries;
};
